import React, { useCallback, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { isValidEmail } from '@totango-app-team/totango-utils/email';
import { Button } from '@totango-app-team/ui-shared-components';
import classNames from 'classnames';
import get from 'lodash/get';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';

import Input from 'Components/input/input';
import RecaptchaWidget from 'Components/recaptcha-widget/recaptcha-widget';
import useFetchCaptchaToken from 'Hooks/useFetchCaptchaToken';
import { domainCaptchaBlacklist, getEmailDomain, isLocal } from 'Utils/utils';

import OauthSignupButton from '../../oauth/oauth-signup-button';
import { gaPushEvent, SIGNUP_METHOD_TYPES } from '../../tracking/google-analytics';
import { trackHubspotSignupDetailes } from '../../tracking/hubspot';
import { validateHumanUserAvailability, validateUserAvailability } from '../../utils/api';
import EmailValidationError from '../../utils/email-validation-error';
import useDecryptEmail from '../../utils/useDecryptEmail';
import { isCommunity } from '../../utils/utils';

import './email-step.less';

export default function EmailStep(props) {
	const {
		handleNext,
		email,
		setEmail,
		setFirstName,
		setLastName,
		setProfileImageUrl,
		setEmailHash,
		setOauthIssuer,
		oauthIssuer,
		captchaEnabled,
	} = props;
	const { fetchToken, captchaRef } = useFetchCaptchaToken();
	const [isValidatingEmail, setIsValidationEmail] = useState(false);
	const [validationErrorReason, setValidationErrorReason] = useState('');
	const { isDecryptingEmail, decryptedEmail, decryptionError } = useDecryptEmail();

	const isRecaptchaFeatureEnabled = captchaEnabled === true || captchaEnabled?.data === true;

	useEffect(() => {
		if (decryptedEmail) {
			setEmail(decryptedEmail);
		}
	}, [decryptedEmail, setEmail]);
	useEffect(() => {
		if (decryptionError) {
			setValidationErrorReason(decryptionError);
		}
	}, [decryptionError]);
	const isLoading = isValidatingEmail || isDecryptingEmail;

	const onButtonClick = useCallback(
		e => {
			const validateUsername = async username => {
				if (!isValidEmail(username)) {
					setIsValidationEmail(false);
					setValidationErrorReason('invalid');
					return;
				}
				let validation = null;
				try {
					const domain = getEmailDomain(username);
					if (isRecaptchaFeatureEnabled && !domainCaptchaBlacklist.includes(domain)) {
						const token = await fetchToken();
						validation = !isString(token)
							? await validateUserAvailability(username)
							: await validateHumanUserAvailability(username, token);
					} else {
						validation = await validateUserAvailability(username);
					}
					if (validation.is_available) {
						setIsValidationEmail(false);
						const signUpMethod = oauthIssuer
							? SIGNUP_METHOD_TYPES.SSO
							: SIGNUP_METHOD_TYPES.STANDARD;
						gaPushEvent({
							stepIndex: 1,
							isSuccess: false,
							isFreeTrial: !isCommunity,
							signUpMethod,
						});
						handleNext();
					} else {
						setEmail('');
						setOauthIssuer(null);
						setIsValidationEmail(false);
						setValidationErrorReason(validation.reason);
					}
				} catch (err) {
					setIsValidationEmail(false);
					setValidationErrorReason('general');
				}
			};
			setValidationErrorReason('');
			setIsValidationEmail(true);
			validateUsername(email);
			trackHubspotSignupDetailes(email);
		},
		[
			email,
			fetchToken,
			handleNext,
			isRecaptchaFeatureEnabled,
			oauthIssuer,
			setEmail,
			setOauthIssuer,
		],
	);

	const onInputChange = useCallback(
		e => {
			const emailAddress = e.target.value.toLowerCase();
			setEmail(emailAddress);
			setValidationErrorReason('');
		},
		[setEmail],
	);

	const onKeyUpHandler = useCallback(
		e => {
			if (e && e.keyCode === 13) {
				onButtonClick();
			}
		},
		[onButtonClick],
	);

	useEffect(() => {
		if (oauthIssuer && email) {
			onButtonClick();
		}
	}, [oauthIssuer, email, onButtonClick]);

	const onOauthSignInSuccess = useCallback(
		async (issuer, user = {}) => {
			setEmail(user.email.toLowerCase());
			setFirstName(get(user, 'firstName'));
			setLastName(get(user, 'lastName'));
			setProfileImageUrl(get(user, 'profileImageUrl'));
			setEmailHash(get(user, 'emailHash'));
			setOauthIssuer(issuer);
		},
		[setEmail, setFirstName, setLastName, setOauthIssuer, setProfileImageUrl, setEmailHash],
	);

	const onOauthError = useCallback(
		error => {
			setValidationErrorReason('general');
		},
		[setValidationErrorReason],
	);

	return (
		<>
			<Grid
				container
				spacing={4}
				className={classNames('step-container create-account email-step-container', {
					captcha: isRecaptchaFeatureEnabled,
				})}
			>
				<Grid item xs={12}>
					<h5 className="step-title" align="center">
						Create your
						<span className="title-underline-container">
							FREE
							<img src="/img/self-service/green-underline.svg" alt="" />
						</span>
						account
					</h5>
				</Grid>
				<Grid item xs={12}>
					{!!validationErrorReason && <EmailValidationError reason={validationErrorReason} />}
					<Input
						id="totango-signup-email"
						className="signup-input-wrapper"
						InputProps={{
							classes: {
								root: 'signup-input',
							},
						}}
						variant="outlined"
						value={email}
						autoFocus
						label="Work Email"
						fullWidth
						autoComplete="no"
						onChange={onInputChange}
						error={!!validationErrorReason}
						onKeyUp={onKeyUpHandler}
						maxLength={100}
						disabled={isLoading}
					/>
				</Grid>
				<Grid item xs={12} className="continue-button-container">
					<Button
						color="primary"
						className="signup-button"
						variant="contained"
						label="Continue"
						onClick={onButtonClick}
						onKeyUp={onKeyUpHandler}
						disabled={isLoading}
						fullWidth
						isLoading={isLoading}
					>
						{'Continue'}
					</Button>
				</Grid>
				<Grid item xs={12} align="center" className="sign-in-with separator">
					OR SIGN UP WITH
				</Grid>
				<Grid container justifyContent="space-evenly">
					{['google', 'slack', 'microsoft'].map(type => (
						<OauthSignupButton
							key={type}
							type={type}
							onSuccess={onOauthSignInSuccess}
							onFailure={onOauthError}
						/>
					))}
				</Grid>
				<Grid item xs={12}>
					<div className="signin-btn-tooltip">
						Already have an account?
						<a
							className="login-link"
							href={
								isLocal()
									? 'https://localhost:8443/debug.html'
									: `https://${window.location.hostname}`
							}
						>
							{' '}
							Login
						</a>
					</div>
				</Grid>
			</Grid>
			{isRecaptchaFeatureEnabled && <RecaptchaWidget ref={captchaRef} />}
		</>
	);
}

EmailStep.defaultProps = {
	oauthIssuer: undefined,
};

EmailStep.propTypes = {
	handleNext: PropTypes.func.isRequired,
	email: PropTypes.string.isRequired,
	setEmail: PropTypes.func.isRequired,
	setFirstName: PropTypes.func.isRequired,
	setLastName: PropTypes.func.isRequired,
	setProfileImageUrl: PropTypes.func.isRequired,
	setOauthIssuer: PropTypes.func.isRequired,
	oauthIssuer: PropTypes.string,
};
